import { memo } from "react";
import { Autocomplete, FormControl, TextField, alpha } from "@mui/material";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

/**
 * Organization selector component
 */
const OrganizationSelector = ({
  organisations,
  selectedOrganisation,
  onChange,
  themeMode,
  theme,
}) => {
  const { t } = useTranslation("common");

  const textColor = themeMode === "dark" ? theme.palette.primary.main : "#fff";

  return (
    <FormControl variant="standard" sx={{ minWidth: 250, mr: 2 }}>
      <Autocomplete
        id="organisation-select"
        aria-label={t("common.select_organisation")}
        options={organisations || []}
        getOptionLabel={(option) => option?.name || ""}
        getOptionKey={(option) => option?.id || ""}
        value={selectedOrganisation || null}
        isOptionEqualToValue={(option, value) => {
          return option?.id === value?.id;
        }}
        loading={!organisations || organisations.length === 0}
        loadingText={t("common.loading")}
        noOptionsText={t("common.no_organisations")}
        onChange={(event, newValue) => onChange(newValue)}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="standard"
            label={t("common.organisation")}
            InputLabelProps={{
              shrink: true,
              sx: { color: alpha(textColor, 0.7) },
            }}
            sx={{
              "& .MuiInput-root": {
                "&::before": {
                  borderBottomColor: alpha(textColor, 0.3),
                },
                "&:hover:not(.Mui-disabled)::before": {
                  borderBottomColor: alpha(textColor, 0.5),
                },
                "&::after": {
                  borderBottomColor: textColor,
                },
              },
              "& .MuiInput-input": {
                color: textColor,
                fontWeight: 500,
              },
              "& .MuiInputLabel-root": {
                color: alpha(textColor, 0.7),
              },
              "& .MuiSvgIcon-root": {
                color: alpha(textColor, 0.7),
                transition: "color 0.2s ease",
                "&:hover": {
                  color: textColor,
                },
              },
            }}
          />
        )}
      />
    </FormControl>
  );
};

OrganizationSelector.propTypes = {
  organisations: PropTypes.array,
  selectedOrganisation: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  themeMode: PropTypes.string.isRequired,
  theme: PropTypes.object.isRequired,
};

export default memo(OrganizationSelector);
